import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EnhancedGitHubButton from './EnhancedGitHubButton';

const Navigation = ({
  navVisible,
  menuOpen,
  theme,
  isAuthenticated,
  aboutDropdownOpen,
  dataDropdownOpen,
  backtestingDropdownOpen,
  toggleMenu,
  setMenuOpen,
  toggleTheme,
  toggleAboutDropdown,
  toggleDataDropdown,
  toggleBacktestingDropdown,
  handleNavItemClick,
  handleLogout,
}) => {
  const { t } = useTranslation();

  return (
    <header className={`App-header ${navVisible ? 'visible' : 'hidden'}`}>
      <div className="nav-container">
        <div className="logo" onClick={() => handleNavItemClick('home')}>
          <Link to="/">AmpyFin</Link>
        </div>
        <nav>
          <div className="hamburger" onClick={toggleMenu}>
            &#9776;
          </div>
          <ul className={`navigation-links ${menuOpen ? 'active' : ''}`}>
            <li className="desktop-only dropdown">
              <div className="dropdown-title" onClick={toggleAboutDropdown}>
                {t('nav.aboutUs')}
                <span className="dropdown-arrow">{aboutDropdownOpen ? '▲' : '▼'}</span>
              </div>
              <ul className={`dropdown-menu ${aboutDropdownOpen ? 'active' : ''}`}>
                <li onClick={() => handleNavItemClick('mission')}>{t('aboutDropdown.mission')}</li>
                <li onClick={() => handleNavItemClick('our-models')}>{t('aboutDropdown.models')}</li>
                <li onClick={() => handleNavItemClick('how-it-works')}>{t('aboutDropdown.howItWorks')}</li>
                <li onClick={() => handleNavItemClick('open-source')}>{t('aboutDropdown.openSource')}</li>
              </ul>
            </li>
            
            <li className="desktop-only dropdown">
              <div className="dropdown-title" onClick={toggleDataDropdown}>
                {t('nav.tradingData')}
                <span className="dropdown-arrow">{dataDropdownOpen ? '▲' : '▼'}</span>
              </div>
              <ul className={`dropdown-menu ${dataDropdownOpen ? 'active' : ''}`}>
                <li onClick={() => handleNavItemClick('portfolio')}>{t('dataDropdown.portfolio')}</li>
                <li onClick={() => handleNavItemClick('rankings')}>{t('dataDropdown.rankings')}</li>
                <li onClick={() => handleNavItemClick('trades')}>{t('dataDropdown.trades')}</li>
                <li onClick={() => handleNavItemClick('benchmark')}>{t('dataDropdown.benchmark')}</li>
              </ul>
            </li>
            
            <li className="desktop-only dropdown">
              <div className="dropdown-title" onClick={toggleBacktestingDropdown}>
                {t('nav.testData')}
                <span className="dropdown-arrow">{backtestingDropdownOpen ? '▲' : '▼'}</span>
              </div>
              <ul className={`dropdown-menu ${backtestingDropdownOpen ? 'active' : ''}`}>
                <li onClick={() => handleNavItemClick('hyper-v3-results')}>{t('testDataDropdown.hyper3')}</li>
                <li onClick={() => handleNavItemClick('prag-v3-results')}>{t('testDataDropdown.prag3')}</li>
                <li onClick={() => handleNavItemClick('hyper-v4-results')}>{t('testDataDropdown.hyper4')}</li>
                <li onClick={() => handleNavItemClick('prag-v4-results')}>{t('testDataDropdown.prag4')}</li>
              </ul>
            </li>
            
            <li className="desktop-only github-tab">
              <EnhancedGitHubButton />
            </li>
            
            {isAuthenticated ? (
              <li className="desktop-only auth-buttons">
                <Link to="/dashboard" className="dashboard-btn">{t('nav.dashboard')}</Link>
                <button onClick={handleLogout} className="logout-btn">{t('nav.logout')}</button>
              </li>
            ) : (
              <li className="desktop-only auth-buttons">
                <Link to="/login" className="login-btn" onClick={() => setMenuOpen(false)}>{t('nav.login')}</Link>
                <Link to="/signup" className="signup-btn" onClick={() => setMenuOpen(false)}>{t('nav.signup')}</Link>
              </li>
            )}
            
            <li className="desktop-only theme-toggle-container">
              <button 
                className="theme-toggle" 
                id="theme-toggle" 
                title="Toggle light & dark theme" 
                aria-label={theme}
                aria-live="polite"
                onClick={toggleTheme}
              >
                <svg className="sun-and-moon" aria-hidden="true" width="24" height="24" viewBox="0 0 24 24">
                  <mask className="moon" id="moon-mask">
                    <rect x="0" y="0" width="100%" height="100%" fill="white" />
                    <circle cx="24" cy="10" r="6" fill="black" />
                  </mask>
                  <circle className="sun" cx="12" cy="12" r="6" mask="url(#moon-mask)" fill="currentColor" />
                  <g className="sun-beams" stroke="currentColor">
                    <line x1="12" y1="1" x2="12" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="23" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                    <line x1="1" y1="12" x2="3" y2="12" />
                    <line x1="21" y1="12" x2="23" y2="12" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                  </g>
                </svg>
              </button>
            </li>
            
            {/* Mobile Navigation */}
            <li className="mobile-only menu-category">PRODUCTS</li>
            <li className="mobile-only" onClick={() => handleNavItemClick('mission')}><div>{t('aboutDropdown.mission')}</div></li>
            <li className="mobile-only" onClick={() => handleNavItemClick('our-models')}><div>{t('aboutDropdown.models')}</div></li>
            <li className="mobile-only" onClick={() => handleNavItemClick('how-it-works')}><div>{t('aboutDropdown.howItWorks')}</div></li>
            <li className="mobile-only" onClick={() => handleNavItemClick('open-source')}><div>{t('aboutDropdown.openSource')}</div></li>
            
            <li className="mobile-only menu-category">DATA</li>
            <li className="mobile-only" onClick={() => handleNavItemClick('portfolio')}><div>{t('dataDropdown.portfolio')}</div></li>
            <li className="mobile-only" onClick={() => handleNavItemClick('rankings')}><div>{t('dataDropdown.rankings')}</div></li>
            <li className="mobile-only" onClick={() => handleNavItemClick('trades')}><div>{t('dataDropdown.trades')}</div></li>
            <li className="mobile-only" onClick={() => handleNavItemClick('benchmark')}><div>{t('dataDropdown.benchmark')}</div></li>
            
            <li className="mobile-only menu-category">TEST DATA</li>
            <li className="mobile-only" onClick={() => handleNavItemClick('hyper-v3-results')}><div>{t('testDataDropdown.hyper3')}</div></li>
            <li className="mobile-only" onClick={() => handleNavItemClick('prag-v3-results')}><div>{t('testDataDropdown.prag3')}</div></li>
            <li className="mobile-only" onClick={() => handleNavItemClick('hyper-v4-results')}><div>{t('testDataDropdown.hyper4')}</div></li>
            <li className="mobile-only" onClick={() => handleNavItemClick('prag-v4-results')}><div>{t('testDataDropdown.prag4')}</div></li>
            
            <div className="mobile-only action-buttons-container">
              <li className="github-tab">
                <EnhancedGitHubButton />
              </li>
            </div>
            
            {isAuthenticated ? (
              <li className="mobile-only auth-buttons">
                <Link to="/dashboard" className="dashboard-btn">{t('nav.dashboard')}</Link>
                <button onClick={handleLogout} className="logout-btn">{t('nav.logout')}</button>
              </li>
            ) : (
              <li className="mobile-only auth-buttons">
                <Link to="/login" className="login-btn" onClick={() => setMenuOpen(false)}>{t('nav.login')}</Link>
                <Link to="/signup" className="signup-btn" onClick={() => setMenuOpen(false)}>{t('nav.signup')}</Link>
              </li>
            )}
            
            <li className="mobile-only mobile-menu-divider"></li>
            
            <li className="mobile-only menu-category">SETTINGS</li>
            <li className="mobile-only theme-toggle-container">
              <button 
                className="theme-toggle" 
                id="mobile-theme-toggle" 
                title="Toggle light & dark theme" 
                aria-label={theme}
                aria-live="polite"
                onClick={toggleTheme}
              >
                <svg className="sun-and-moon" aria-hidden="true" width="24" height="24" viewBox="0 0 24 24">
                  <mask className="moon" id="mobile-moon-mask">
                    <rect x="0" y="0" width="100%" height="100%" fill="white" />
                    <circle cx="24" cy="10" r="6" fill="black" />
                  </mask>
                  <circle className="sun" cx="12" cy="12" r="6" mask="url(#mobile-moon-mask)" fill="currentColor" />
                  <g className="sun-beams" stroke="currentColor">
                    <line x1="12" y1="1" x2="12" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="23" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                    <line x1="1" y1="12" x2="3" y2="12" />
                    <line x1="21" y1="12" x2="23" y2="12" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                  </g>
                </svg>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Navigation;
